import { gql, useMutation } from '@apollo/client';
import IconError from '@apollo/icons/default/IconError.svg';
import IconUnavailable from '@apollo/icons/default/IconUnavailable.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import IconChevronDown from '@apollo/icons/small/IconChevronDown.svg';
import {
  Button,
  CardSection,
  CardSectionControl,
  CardSectionDescription,
  CardSectionTitle,
  Code,
  Group,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useState } from 'react';

import { variantSettingsRouteConfig } from 'src/app/graph/routes';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalChangeMismatchSeverity } from 'src/lib/graphqlTypes/types';

export const ProposalsCheckConfigVariantFragment = gql`
  fragment ProposalsCheckConfigVariantFragment on VariantCheckConfiguration {
    id
    proposalChangeMismatchSeverityConfig {
      proposalChangeMismatchSeverity
    }
  }
`;

export const variantCheckConfigProposalChangeMismatchSeverityMutation = gql`
  mutation VariantCheckConfigProposalChangeMismatchSeverityMutation(
    $graphId: ID!
    $graphVariant: String!
    $proposalChangeMismatchSeverity: ProposalChangeMismatchSeverity
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationProposalChangeMismatchSeverity(
          proposalChangeMismatchSeverity: $proposalChangeMismatchSeverity
        ) {
          id
          proposalChangeMismatchSeverityConfig {
            proposalChangeMismatchSeverity
          }
        }
      }
    }
  }
`;

/**
 * TODO(rebrand): remove the `important`s here once icon colors can be overridden within buttons
 */

const SeverityItem = ({
  severity,
  className,
}: {
  severity: ProposalChangeMismatchSeverity;
  className?: string;
}) => {
  switch (severity) {
    case ProposalChangeMismatchSeverity.ERROR:
      return (
        <div className={classnames(className)}>
          <IconError className="mr-2 align-[-1px] !text-icon-error" />
          Error
        </div>
      );
    case ProposalChangeMismatchSeverity.WARN:
      return (
        <div className={classnames(className)}>
          <IconWarning className="mr-2 align-[-1px] !text-icon-warning" />
          Warn
        </div>
      );
    case ProposalChangeMismatchSeverity.OFF:
      return (
        <div className={classnames(className)}>
          <IconUnavailable className="mr-2 align-[-1px] !text-icon-secondary" />
          Off
        </div>
      );
    default:
      return assertUnreachable(severity);
  }
};

export const ProposalCheckSection = ({
  graphId,
  graphVariant,
  variantCheckConfig,
}: {
  graphId: string;
  graphVariant: string;
  variantCheckConfig?: GraphQLTypes.ProposalsCheckConfigVariantFragment;
}) => {
  const isVariantConfig = Boolean(variantCheckConfig);

  const [selectedSeverity, setSelectedSeverity] = useState(
    variantCheckConfig?.proposalChangeMismatchSeverityConfig
      ?.proposalChangeMismatchSeverity ?? ProposalChangeMismatchSeverity.OFF,
  );

  const [updateProposalVariantCheckConfig, { loading }] = useMutation<
    GraphQLTypes.VariantCheckConfigProposalChangeMismatchSeverityMutation,
    GraphQLTypes.VariantCheckConfigProposalChangeMismatchSeverityMutationVariables
  >(variantCheckConfigProposalChangeMismatchSeverityMutation);

  const onSelect = useCallback(
    async (severity: ProposalChangeMismatchSeverity) => {
      if (severity !== selectedSeverity) {
        setSelectedSeverity(severity);
        if (isVariantConfig) {
          await updateProposalVariantCheckConfig({
            variables: {
              graphId,
              graphVariant,
              proposalChangeMismatchSeverity: severity,
            },
          });
        }
      }
    },
    [
      graphId,
      graphVariant,
      selectedSeverity,
      isVariantConfig,
      updateProposalVariantCheckConfig,
    ],
  );

  return (
    <CardSection className="flex-row">
      <Group orientation="vertical">
        <CardSectionTitle>
          Add a "Proposals" task to your schema checks
        </CardSectionTitle>
        <CardSectionDescription>
          This configures the <Code>Proposals</Code> task in schema checks. The{' '}
          <Code>Proposals</Code> task looks for proposals associated with this
          graph and compares their diffs against the changes being checked. You
          can turn the task off or configure it to return a warning or error if
          a matching proposal isn't found.
        </CardSectionDescription>
      </Group>
      {isVariantConfig ? (
        <CardSectionControl>
          <Menu placement="bottom-end">
            <PermissionWarning
              graphId={graphId}
              graphPermissions="canWriteCheckConfiguration"
              childRenderer={(hasPermission) => (
                <MenuButton
                  as={Button}
                  variant="secondary"
                  isLoading={loading}
                  isDisabled={!hasPermission}
                  rightIcon={<IconChevronDown />}
                >
                  {selectedSeverity ? (
                    <SeverityItem severity={selectedSeverity} />
                  ) : (
                    'Select task severity'
                  )}
                </MenuButton>
              )}
            />
            <MenuContent>
              <MenuList>
                <MenuOptionGroup
                  label="IF A MATCHING PROPOSAL IS NOT FOUND..."
                  value={selectedSeverity}
                  type="radio"
                  onChange={onSelect}
                >
                  <MenuItemOption
                    type="radio"
                    value={ProposalChangeMismatchSeverity.ERROR}
                  >
                    <SeverityItem
                      severity={ProposalChangeMismatchSeverity.ERROR}
                    />
                  </MenuItemOption>
                  <MenuItemOption
                    type="radio"
                    value={ProposalChangeMismatchSeverity.WARN}
                  >
                    <SeverityItem
                      severity={ProposalChangeMismatchSeverity.WARN}
                    />
                  </MenuItemOption>
                  <MenuDivider />
                  <MenuItemOption
                    type="radio"
                    value={ProposalChangeMismatchSeverity.OFF}
                  >
                    <SeverityItem
                      severity={ProposalChangeMismatchSeverity.OFF}
                    />
                  </MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </MenuContent>
          </Menu>
        </CardSectionControl>
      ) : (
        <Button
          variant="secondary"
          as={Link}
          to={variantSettingsRouteConfig.path({
            graphId,
            graphVariant,
            tab: 'checks_proposals',
          })}
        >
          Variant setting
        </Button>
      )}
    </CardSection>
  );
};
